import { constants as deviceConstants } from '@saucelabs/sl-component-device';

import appSettingsLanguages from './constantAppSettingsLanguages.json';

export const NAMESPACE = 'manual';

export const MAX_APP_BUILD_DESC_CHAR_BYTE_SIZE = 3;
export const MAX_APP_BUILD_DESC_LENGTH = 255;

export const MIN_WEBRTC_VERSION_ANDROID = '10.0.0';
export const MIN_WEBRTC_VERSION_IOS = '13.2.0';

export const PERFORMANCE_MODE_ENABLED_KEY = `${NAMESPACE} - Performance Mode`;

export const APP_TESTING_ROUTE_ID_TYPE = {
  GROUP: 'group',
  FILE: 'file',
};

export const APP_STORAGE_FETCH_GROUP_PAGE_SIZE = 25;
export const APP_STORAGE_FETCH_FILES_PAGE_SIZE = 25;

export const APP_STORAGE_KIND = Object.freeze({
  IOS: 'ios',
  ANDROID: 'android',
  OTHER: 'other',
} as const);

export const APP_STORAGE_PRETTY_PLATFORM = Object.freeze({
  [APP_STORAGE_KIND.IOS]: 'iOS',
  [APP_STORAGE_KIND.ANDROID]: 'Android',
} as const);

export const APP_STORAGE_OS_FILTERS = ['ios', 'android'];

export const APP_STORAGE_SETTING_LANGUAGES = appSettingsLanguages;
export const APP_STORAGE_SETTING_ORIENTATIONS = Object.freeze({
  // default value is null and sets orientation to device default orientation in the backend
  DEFAULT: 'DEFAULT',
  LANDSCAPE: 'LANDSCAPE',
  PORTRAIT: 'PORTRAIT',
} as const);

export const APP_UPLOAD_STATUS = Object.freeze({
  CANCELED: 'CANCELED',
  FAILED: 'FAILED',
  UPLOADED: 'UPLOADED',
  UPLOADING: 'UPLOADING',
  PROCESSING: 'PROCESSING',
} as const);
export const APP_UPLOAD_ERROR_MESSAGE = 'Upload failed';
export const APP_UPLOAD_CANCEL_MESSAGE = 'Upload canceled';

export const MOBILE_DEVICE_HARDWARE_BUTTON_TYPES = Object.freeze({
  BACK: 'BACK',
  HOME: 'HOME',
  MENU: 'MENU',
  SEARCH: 'SEARCH',
} as const);

export const FORM_TAB_STATES = Object.freeze({
  WEB_DEVICE_TYPE: {
    DESKTOP: 'DESKTOP',
    MOBILE_VIRTUAL: 'MOBILE_VIRTUAL',
    MOBILE_REAL: 'MOBILE_REAL',
  },
  PLATFORM_TYPE: {
    WEB: 'WEB',
    MOBILE: 'MOBILE',
  },
  DEVICE: {
    DESKTOP: 'DESKTOP',
    MOBILE: 'MOBILE',
  },
  DEVICE_TYPE: {
    REAL: 'REAL',
    VIRTUAL: 'VIRTUAL',
  },
} as const);

export const CONFIGURATION_TEST_TYPES = Object.freeze({
  APP: 'APP',
  WEB: 'WEB',
} as const);

export const CONFIGURATION_DEVICE_TYPES = Object.freeze({
  DESKTOP_BROWSER: 'DESKTOP_BROWSER',
  REAL_DEVICE: 'REAL_DEVICE',
  VIRTUAL_DEVICE: 'VIRTUAL_DEVICE',
} as const);

export const CONFIGURATION_COMBINATION_TYPES = Object.freeze({
  WEB_DESKTOP_BROWSER: 'WEB_DESKTOP_BROWSER',
  WEB_REAL_DEVICE: 'WEB_REAL_DEVICE',
  WEB_VIRTUAL_DEVICE: 'WEB_VIRTUAL_DEVICE',
  APP_REAL_DEVICE: 'APP_REAL_DEVICE',
  APP_VIRTUAL_DEVICE: 'APP_VIRTUAL_DEVICE',
} as const);

export const TEST_CONFIGURATION_TABS = Object.freeze({
  DESKTOP: 'DESKTOP',
  MOBILE_REAL: 'MOBILE_REAL',
  MOBILE_VIRTUAL: 'MOBILE_VIRTUAL',
} as const);

export const SESSION_TYPE = Object.freeze({
  DESKTOP: 'DESKTOP',
  MOBILE_REAL: 'MOBILE_REAL',
  MOBILE_VIRTUAL: 'MOBILE_VIRTUAL',
} as const);

export const CONFIGURATION_MOBILE_APP_TAB_URL_PATHS = Object.freeze({
  [TEST_CONFIGURATION_TABS.MOBILE_REAL]: 'device',
  [TEST_CONFIGURATION_TABS.MOBILE_VIRTUAL]: 'virtual',
} as const);

export const DEVICE_TYPE = Object.freeze({
  DESKTOP_BROWSER: 'DESKTOP_BROWSER',
  EMULATOR: 'EMULATOR',
  SIMULATOR: 'SIMULATOR',
  REAL_DEVICE: 'REAL_DEVICE',
} as const);

export const CREATE_MOBILE_SESSION_ERRORS = Object.freeze({
  DEVICE_NOT_AVAILABLE: 'DEVICE_NOT_AVAILABLE',
  DEVICE_NOT_INCLUDED_IN_PLAN: 'DEVICE_NOT_INCLUDED_IN_PLAN',
  PARALLEL_SESSIONS_LIMIT_REACHED: 'PARALLEL_SESSIONS_LIMIT_REACHED',
  SAUCE_CONNECT_REQUIRED: 'SAUCE_CONNECT_REQUIRED',
} as const);

export const CREATE_MOBILE_SESSION_ERROR_MESSAGES = Object.freeze({
  DEVICE_NOT_AVAILABLE: {
    header: '',
    text: 'Device currently in use.',
  },
  DEVICE_NOT_INCLUDED_IN_PLAN: {
    header: '',
    text: 'Device not included in your plan. Please upgrade.',
  },
  PARALLEL_SESSIONS_LIMIT_REACHED: {
    header: '',
    text: 'Limit of parallel sessions reached. Please upgrade.',
  },
  SAUCE_CONNECT_REQUIRED: {
    header: 'Misconfigured: Cannot create a session without a Sauce Connect tunnel.',
    text: 'Your account settings only allow tests to run with a Sauce Connect tunnel for proxying. ' +
      'Please make sure you have a Sauce Connect tunnel selected for this test. Please contact your account administrator.',
  },
} as const);

export const CREATE_MOBILE_SESSION_ERROR_MESSAGE_DEFAULT = {
  header: '',
  text: 'Session could not be created.',
};

export const MOBILE_SESSION_STATES = Object.freeze({
  SESSION_CREATION_FAILED: 'SESSION_CREATION_FAILED',
  DEVICE_CONNECTION_FAILED: 'DEVICE_CONNECTION_FAILED',
  DEVICE_DISCONNECTED: 'DEVICE_DISCONNECTED',
  APP_INSTALLATION_FAILED: 'APP_INSTALLATION_FAILED',
  INITIAL: 'INITIAL',
  REQUESTING: 'REQUESTING',
  ALLOCATING: 'ALLOCATING',
  PREPARING: 'PREPARING',
  INSTALLING: 'INSTALLING',
  READY: 'READY',
} as const);

export const MOBILE_SESSION_STATE_VALUES = Object.freeze({
  SESSION_CREATION_FAILED: {
    duration: 0,
    message: 'Session creation failed',
    percent: 0,
    state: deviceConstants.STATES.ERROR,
  },
  DEVICE_CONNECTION_FAILED: {
    duration: 0,
    message: 'Device connection failed',
    percent: 0,
    state: deviceConstants.STATES.ERROR,
  },
  DEVICE_DISCONNECTED: {
    duration: 0,
    message: 'Device disconnected',
    percent: 0,
    state: deviceConstants.STATES.ERROR,
  },
  APP_INSTALLATION_FAILED: {
    duration: 0,
    message: 'App installation failed',
    percent: 0,
    state: deviceConstants.STATES.ERROR,
  },
  INITIAL: {
    duration: 0,
    message: 'Loading...',
    percent: 0,
    state: deviceConstants.STATES.PROGRESS,
  },
  REQUESTING: {
    duration: 1,
    message: 'Requesting...',
    percent: 10,
    state: deviceConstants.STATES.PROGRESS,
  },
  ALLOCATING: {
    duration: 2,
    message: 'Allocating a device...',
    percent: 20,
    state: deviceConstants.STATES.PROGRESS,
  },
  PREPARING: {
    duration: 30,
    message: 'Preparing the device...',
    percent: 80,
    state: deviceConstants.STATES.PROGRESS,
  },
  INSTALLING: {
    duration: 5,
    message: 'Installing your app...',
    percent: 99,
    state: deviceConstants.STATES.PROGRESS,
  },
  READY: {
    duration: 1,
    message: 'Ready',
    percent: 100,
    state: deviceConstants.STATES.INFO,
  },
} as const);

export const MOBILE_DEVICE_STATES = Object.freeze({
  OFFLINE: 'OFFLINE',
  CLOSING: 'CLOSING',
  LAUNCH: 'LAUNCH',
  VIDEO: 'VIDEO',
  BOOTED: 'BOOTED',
  INPUT: 'INPUT',
  ONLINE: 'ONLINE',
  UI: 'UI',
} as const);

export const MOBILE_DEVICE_ORIENTATIONS = Object.freeze({
  LANDSCAPE: 'LANDSCAPE',
  PORTRAIT: 'PORTRAIT',
} as const);

export const OS_FILTERS = ['IOS', 'ANDROID'];

export const LAST_CHANGE_TIME_FORMAT = 'MMM dd, yyyy H:mm O';

export const SESSION_DEFAULT_URL = 'https://www.saucedemo.com/';
export const APP_TESTING_WIKI_URL = 'https://docs.saucelabs.com/mobile-apps/live-testing/live-mobile-app-testing/index.html';
export const UP_RDC_TESTING_PHASE_2_WIKI_URL = 'https://wiki.saucelabs.com/display/DOCS/2020/07/27/Announcing+Improved+UX+for+Real+Device+Testing+on+Sauce+Labs%3A+Phase+2';
export const TRAINING_SUPPORT_URL = 'https://saucelabs.com/training-support';

export const APP_STORAGE_FILE_ID_PREFIX = 'storage:';

export const APP_STORAGE_SETTINGS = Object.freeze({
  AUDIO_ANDROID: {
    id: 'audio_capture',
    kind: APP_STORAGE_KIND.ANDROID,
    text: 'App Audio',
    info: 'Enables you to capture audio from your app during tests.',
  },
  AUDIO_IOS: {
    id: 'audio_capture',
    kind: APP_STORAGE_KIND.IOS,
    text: 'OS Audio',
    info: 'Enables you to capture audio from the iOS/iPadOS devices during tests.',
  },
  LANG: {
    id: 'lang',
    text: 'Device Language',
  },
  ORIENTATION: {
    id: 'orientation',
    text: 'Device Orientation',
  },
  PASSCODE_ANDROID: {
    id: 'setup_device_lock',
    info: 'Enables you to automatically populate the screenlock and PIN on the device.',
    text: 'Device passcode',
  },
  PASSCODE_IOS: {
    id: 'setup_device_lock',
    info: 'Enables you to automatically populate the passcode on the device. Please note that the device allocation time is slightly increased with this capability.',
    text: 'Device passcode',
  },
  PROXY: {
    id: 'proxy',
    text: 'Proxy Settings',
  },
  PROXY_ENABLED: {
    id: 'proxy_enabled',
    text: 'Proxy',
  },
  HOST: {
    id: 'host',
    text: 'Hostname',
  },
  PORT: {
    id: 'port',
    text: 'Port',
  },
  IMAGE_INJECTION: {
    id: 'image_injection',
    info: 'Enables you to mimic camera behavior when testing applications by letting you upload an image and present it to the application as if it was read by the device camera.',
    text: 'Image Injection',
  },
  BYPASS_SCREENSHOT_RESTRICTION: {
    id: 'bypass_screenshot_restriction',
    info: 'Enables you to take screenshots of your app during tests, even if your app does not allow screenshots for security reasons.',
    text: 'Bypass Screenshot Restriction',
  },
  BIOMETRICS_IOS: {
    id: 'biometrics',
    info: 'Allows you to choose authentication options if your mobile app requires a biometric authentication, either Touch ID or Face ID.',
    text: 'Biometrics Interception',
  },
  BIOMETRICS_ANDROID: {
    id: 'biometrics',
    info: 'Allows you to choose authentication options if your mobile app requires a biometric authentication, either fingerprint recognition or face recognition.',
    text: 'Biometrics Interception',
  },
  NETWORK_CAPTURE: {
    id: 'network_capture',
    info: 'Enables you to capture network packets during tests to see all HTTP request and responses.',
    text: 'Network Capture',
  },
  INSTRUMENTATION: {
    id: 'instrumentation',
    text: 'Instrumentation Settings',
  },
  INSTRUMENTATION_ENABLED: {
    id: 'instrumentation_enabled',
    info: 'Allows you to use advanced features when testing your app in the real device cloud, like Image Injection and screenshotting secure views.',
    text: 'Instrumentation',
  },
  RESIGNING: {
    id: 'resigning',
    text: 'Resigning Settings',
  },
  RESIGNING_ENABLED: {
    id: 'resigning_enabled',
    info: 'Enables re-signing of iOS apps, which is required to run tests in the real device cloud. Re-signed apps can also use advanced features, like Image Injection. Disabling this option can cause application installation failures.',
    // Changed to "Instrumentation" for UI consistency between Android and iOS apps
    text: 'Instrumentation',
    warningWhenDisabled: 'You cannot run live or automated tests on public devices when this option is disabled.',
  },
  SYS_ALERTS_DELAY: {
    id: 'sys_alerts_delay',
    info: 'Delay system alerts, such as asking for permission to access the camera, to prevent app crashes at startup.',
    text: 'System Alerts Delay',
  },
  GROUP_DIRECTORY: {
    id: 'group_directory',
    info: 'Enables you to use your app’s private app container directory instead of the shared app group container directory. When your app gets resigned, the shared directory is not accessible.',
    text: 'Group Folder Redirect',
  },
} as const);

export const ALLOWED_ANDROID_SETTINGS = [
  APP_STORAGE_SETTINGS.BIOMETRICS_ANDROID.id,
  APP_STORAGE_SETTINGS.IMAGE_INJECTION.id,
  APP_STORAGE_SETTINGS.BYPASS_SCREENSHOT_RESTRICTION.id,
  APP_STORAGE_SETTINGS.NETWORK_CAPTURE.id,
];

export const ALLOWED_IOS_SETTINGS = [
  APP_STORAGE_SETTINGS.IMAGE_INJECTION.id,
  APP_STORAGE_SETTINGS.BIOMETRICS_IOS.id,
  APP_STORAGE_SETTINGS.GROUP_DIRECTORY.id,
  APP_STORAGE_SETTINGS.SYS_ALERTS_DELAY.id,
  APP_STORAGE_SETTINGS.NETWORK_CAPTURE.id,
];

export const LS_PUBLIC_CLOUD_WARNING_DISMISSED = 'LS_PUBLIC_CLOUD_WARNING_DISMISSED';

export const RDC_DEVICE_RESPONSE_STATUS = Object.freeze({
  ABORTED: 'ABORTED',
  ERROR: 'ERROR',
  FINISHED: 'FINISHED',
  PENDING: 'PENDING',
} as const);

export const SESSION_EXPIRY_WARNING_START_TIME_S = 180;

export enum SESSION_INACTIVE_REASONS {
  CLOSED_BY_USER = 'CLOSED_BY_USER',
  EXPIRED = 'EXPIRED',
  INACTIVE = 'INACTIVE',
  RECONNECTION_FAILED = 'RECONNECTION_FAILED',
  UNKNOWN = 'UNKNOWN',
}
